import BusinessIcon from '@material-design-icons/svg/filled/business.svg?react';
import EditIcon from '@material-design-icons/svg/filled/edit.svg?react';
import LockIcon from '@material-design-icons/svg/filled/lock.svg?react';
import LockOpenIcon from '@material-design-icons/svg/filled/lock_open.svg?react';
import PersonIcon from '@material-design-icons/svg/filled/person.svg?react';
import RefreshIcon from '@material-design-icons/svg/filled/refresh.svg?react';
import { use, useCallback, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import {
  BackLink,
  HeaderAction,
  headerIconButtonClassName,
  MetaSeparator,
} from '../../components/common/detailChrome';
import { Chip } from '../../components/common/detailListParts';
import { CopyableValue } from '../../components/common/detailParts';
import {
  StatusBadge,
  TypedStatusBadge,
} from '../../components/common/StatusBadge';
import {
  TabNavigation,
  tabId,
  tabPanelId,
} from '../../components/common/TabNavigation';
import { ConfirmationDialog } from '../../components/ConfirmationDialog';
import { S3Image } from '../../components/S3Image';
import { TRPCContext } from '../../contexts/TRPC';
import { AuthStateType } from '../../hooks/useAuth';
import { usePageTitle } from '../../hooks/usePageTitle';
import { useRequireAuth } from '../../hooks/useRequireAuth';
import { formatRelativeTime } from '../../lib/formatters';
import { cn } from '../../lib/utils';
import { hasAdminAccess } from '../../utils/roleUtils';
import { formatDateTimeWithLocalZone } from '../../utils/stringHelpers';
import { PublisherProfileDialog } from './PublisherProfileDialog';
import { publisherDisplayName, publisherWebsite } from './publisherSignals';
import { superPublisherTabs } from './tabConfig';
import type { SuperPublisherDetailData, SuperPublisherTabKey } from './types';
import { useSuperPublisherDetail } from './useSuperPublisherDetail';

/** Members and KYB expose account PII, so they stay admin-only. */
const ADMIN_ONLY_TAB_KEYS = new Set<SuperPublisherTabKey>(['members', 'kyb']);

function formatDate(timestamp: number | null): string {
  return timestamp ? formatDateTimeWithLocalZone(timestamp) : '—';
}

function hostnameOf(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
}

export function SuperPublisherDetailPage() {
  const { id: publisherUserJoinId } = useParams<{ id: string }>();
  const { authState, canRender } = useRequireAuth({
    requireRole: 'publisherStaff',
    redirectUnauthorizedTo: '/dashboard',
  });
  const canManagePublisher =
    authState.type === AuthStateType.Authenticated &&
    hasAdminAccess(authState.user.role, authState.user.email);
  const navigate = useNavigate();
  const trpc = use(TRPCContext);
  const [searchParams, setSearchParams] = useSearchParams();

  const [isFreezeDialogOpen, setIsFreezeDialogOpen] = useState(false);
  const [isUpdatingFreeze, setIsUpdatingFreeze] = useState(false);
  const [isProfileDialogOpen, setIsProfileDialogOpen] = useState(false);

  const handleLoadError = useCallback(
    (message: string) => {
      toast.error(message);
      navigate('/publishers');
    },
    [navigate],
  );

  const { data, isLoading, reload } = useSuperPublisherDetail({
    publisherUserJoinId,
    trpc,
    onError: handleLoadError,
  });

  usePageTitle(
    data
      ? `${publisherDisplayName(data.publisher)} | Publishers`
      : 'Publisher | Publishing Portal',
  );

  const visibleTabs = canManagePublisher
    ? superPublisherTabs
    : superPublisherTabs.filter((tab) => !ADMIN_ONLY_TAB_KEYS.has(tab.key));
  const requestedTab = searchParams.get('tab');
  const activeTabDefinition =
    visibleTabs.find((tab) => tab.key === requestedTab) ?? visibleTabs[0];
  const ActiveTabComponent = activeTabDefinition.component;

  const setActiveTab = useCallback(
    (tab: SuperPublisherTabKey) => {
      setSearchParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          if (tab === 'overview') {
            next.delete('tab');
          } else {
            next.set('tab', tab);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  if (!canRender) return null;

  if (!data) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <div className="text-sm text-neutral-400">
          Loading publisher details…
        </div>
      </div>
    );
  }

  const { publisher, members, dapps } = data;
  const nextFreezeState = !publisher.isFrozen;

  const handleConfirmFreezeToggle = async () => {
    setIsFreezeDialogOpen(false);
    setIsUpdatingFreeze(true);

    try {
      const result = await trpc.client.updateSuperPublisherFreeze.mutate({
        publisherUserJoinId: publisher.id,
        isFrozen: nextFreezeState,
      });

      if (result._tag === 'Left') {
        throw new Error(result.left.message);
      }

      toast.success(result.right.message);
      await reload();
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : 'Failed to update publisher freeze state',
      );
    } finally {
      setIsUpdatingFreeze(false);
    }
  };

  const tabCounts: Partial<Record<SuperPublisherTabKey, number>> = {
    dapps: dapps.length,
    members: members.length,
  };

  return (
    <>
      <div className="flex-1 overflow-y-auto">
        <div className="sticky top-0 z-20 border-b border-white/5 bg-[#161717]/95 backdrop-blur-xl">
          <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between gap-3 pt-3">
              <BackLink to="/publishers">Publishers</BackLink>
              <div className="flex items-center gap-1.5">
                {canManagePublisher && (
                  <>
                    <HeaderAction
                      icon={<EditIcon className="size-3.5 fill-current" />}
                      onClick={() => setIsProfileDialogOpen(true)}
                    >
                      Edit profile
                    </HeaderAction>
                    <HeaderAction
                      icon={
                        publisher.isFrozen ? (
                          <LockOpenIcon className="size-3.5 fill-current" />
                        ) : (
                          <LockIcon className="size-3.5 fill-current" />
                        )
                      }
                      tone={publisher.isFrozen ? 'primary' : 'caution'}
                      disabled={isUpdatingFreeze}
                      onClick={() => setIsFreezeDialogOpen(true)}
                    >
                      {isUpdatingFreeze
                        ? 'Updating…'
                        : publisher.isFrozen
                          ? 'Unfreeze'
                          : 'Freeze'}
                    </HeaderAction>
                  </>
                )}
                <button
                  type="button"
                  aria-label="Reload publisher"
                  title="Reload"
                  disabled={isLoading}
                  onClick={() => {
                    void reload();
                  }}
                  className={headerIconButtonClassName}
                >
                  <RefreshIcon
                    className={cn(
                      'size-4 fill-current',
                      isLoading && 'animate-spin',
                    )}
                  />
                </button>
              </div>
            </div>

            <IdentityHeader data={data} showAccount={canManagePublisher} />

            <TabNavigation
              tabs={visibleTabs}
              activeTab={activeTabDefinition.key}
              counts={tabCounts}
              idPrefix="publisher"
              ariaLabel="Publisher sections"
              onTabChange={setActiveTab}
            />
          </div>
        </div>

        <div
          id={tabPanelId('publisher', activeTabDefinition.key)}
          role="tabpanel"
          aria-labelledby={tabId('publisher', activeTabDefinition.key)}
          className="mx-auto w-full max-w-6xl px-4 py-5 sm:px-6 lg:px-8"
        >
          <ActiveTabComponent
            data={data}
            formatDate={formatDate}
            reload={reload}
            canManagePublisher={canManagePublisher}
            onTabChange={setActiveTab}
            onEditProfile={() => setIsProfileDialogOpen(true)}
          />
        </div>
      </div>

      <ConfirmationDialog
        isOpen={isFreezeDialogOpen}
        title={nextFreezeState ? 'Freeze publisher' : 'Unfreeze publisher'}
        message={
          nextFreezeState
            ? 'This will prevent users with this publisher active from adding dApps or uploading new versions. Existing apps and review workflows remain available.'
            : 'Users with this publisher active will be able to add dApps and upload new versions again.'
        }
        confirmText={nextFreezeState ? 'Freeze' : 'Unfreeze'}
        cancelText="Cancel"
        onConfirm={handleConfirmFreezeToggle}
        onCancel={() => setIsFreezeDialogOpen(false)}
      />

      {canManagePublisher && (
        <PublisherProfileDialog
          open={isProfileDialogOpen}
          onOpenChange={setIsProfileDialogOpen}
          publisher={publisher}
          onSaved={reload}
        />
      )}
    </>
  );
}

/** Avatar, name, standing chips, and the one-line facts an admin scans first. */
function IdentityHeader({
  data,
  showAccount,
}: {
  data: SuperPublisherDetailData;
  showAccount: boolean;
}) {
  const { publisher, stats } = data;
  const name = publisherDisplayName(publisher);
  const website = publisherWebsite(publisher);
  const TypeIcon =
    publisher.type === 'Organization' ? BusinessIcon : PersonIcon;
  const fallback = (
    <div className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-white/[0.04] ring-1 ring-white/10">
      <TypeIcon className="size-6 fill-current text-neutral-500" />
    </div>
  );

  return (
    <div className="flex items-start gap-3.5 pb-4 pt-3">
      {publisher.individual ? (
        <S3Image
          src={publisher.individual.avatarUrl}
          thumbnailSrc={publisher.individual.avatarThumbnailUrl}
          alt=""
          aria-hidden="true"
          className="size-12 shrink-0 rounded-xl object-cover ring-1 ring-white/10"
          fallback={fallback}
        />
      ) : (
        fallback
      )}
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="truncate text-xl font-semibold tracking-tight text-white sm:text-2xl">
            {name}
          </h1>
          <Chip>{publisher.type}</Chip>
          {publisher.kybSubmission && (
            <TypedStatusBadge
              type="kyb"
              status={publisher.kybSubmission.status}
            />
          )}
          {publisher.isFrozen && (
            <StatusBadge status="Frozen" variant="error" />
          )}
          {publisher.kycKybBypassEnabled && (
            <StatusBadge status="KYC/B bypass" variant="yellow" />
          )}
          {showAccount && publisher.isOwner && (
            <StatusBadge status="Owner" variant="info" />
          )}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-xs text-neutral-500">
          {showAccount && publisher.user.email && (
            <>
              <CopyableValue
                value={publisher.user.email}
                mono={false}
                className="-my-1.5 max-w-full"
              />
              <MetaSeparator />
            </>
          )}
          {website && (
            <>
              <a
                href={website}
                target="_blank"
                rel="noopener noreferrer"
                title={website}
                className="truncate text-teal-400 transition-colors hover:text-teal-300"
              >
                {hostnameOf(website)}
              </a>
              <MetaSeparator />
            </>
          )}
          <span className="tabular-nums">
            {stats.totalMembers}{' '}
            {stats.totalMembers === 1 ? 'member' : 'members'}
          </span>
          <MetaSeparator />
          <span className="tabular-nums">
            {stats.totalDapps} {stats.totalDapps === 1 ? 'dapp' : 'dapps'}
            {stats.publishedDapps > 0 ? ` · ${stats.publishedDapps} live` : ''}
          </span>
          <MetaSeparator />
          <span title={formatDate(publisher.acceptedAt ?? publisher.created)}>
            Joined{' '}
            {formatRelativeTime(publisher.acceptedAt ?? publisher.created)}
          </span>
        </div>
      </div>
    </div>
  );
}
